import { locations as datasetLocations } from '@/data/locations';

// Utility to normalize text for matching
const normText = (s: any) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\\p{L}\\p{N}\\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

const normRegion = (r?: string) => {
  const key = normText(r);
  const map: Record<string, string> = {
    'al madinah': 'medina',
    'madinah': 'medina',
    'medinah': 'medina',
    'al madinah al munawwarah': 'medina',
    'ar riyadh': 'riyadh'
  };
  return map[key] || key;
};

const normOwner = (o?: string) => {
  const key = normText(o);
  const map: Record<string, string> = {
    'tableads': 'tableads',
    'alan': 'alan',
    'waki': 'waki',
    'attention': 'attention'
  };
  return map[key] || key;
};

// Robust normalized name matching: strip leading indices like "1 - ", unify dashes, singularize "screens"
const normName = (s: any) => {
  const raw = (s ?? '').toString().replace(/^\s*\d+\s*[-–—.).]\s*/, '');
  const base = normText(raw);
  return base.replace(/\bscreens\b/g, 'screen');
};

/**
 * Resolve AI-suggested locations with ID-first matching, falling back to strict name equality.
 * - If suggestion has a valid id, use dataset location with that id
 * - Otherwise, exact normalized name equality only
 * - If multiple name matches, narrow by region and mediaOwner when present
 */
export function resolveSuggestedLocations(suggested: any[], dataset: typeof datasetLocations = datasetLocations) {
  const resolved = suggested
    .map((sugg) => {
      if (!(typeof sugg?.id === 'number' || sugg?.name)) return null;

      // Prefer exact ID match when provided
      if (typeof sugg?.id === 'number') {
        const byId = dataset.find(loc => loc.id === sugg.id);
        if (byId) {
          return {
            ...byId,
            devices: sugg.devices ?? byId.devices,
            reasoning: sugg.reasoning,
            aiSelected: true,
            id: byId.id,
          } as any;
        }
      }

      const nameKey = normName(sugg.name);
      const regionKey = normRegion(sugg.region);
      const ownerKey = normOwner(sugg.mediaOwner);

      // Exact name candidates only
      let candidates = dataset.filter(loc => normName(loc.name) === nameKey);

      // Disambiguate by region/owner when available
      if (candidates.length > 1 && regionKey) {
        candidates = candidates.filter(loc => normRegion(loc.region) === regionKey);
      }
      if (candidates.length > 1 && ownerKey) {
        candidates = candidates.filter(loc => normOwner(loc.mediaOwner) === ownerKey);
      }

      const actual = candidates?.[0];

      if (actual) {
        return {
          ...actual,
          devices: sugg.devices ?? actual.devices,
          reasoning: sugg.reasoning,
          aiSelected: true,
          id: actual.id,
        } as any;
      }

      // Fallback minimal object (will not preselect on the map but keeps UI consistent)
      return {
        name: sugg.name,
        devices: sugg.devices || 1,
        mediaOwner: sugg.mediaOwner,
        region: sugg.region,
        type: sugg.type || 'Digital Screen',
        reasoning: sugg.reasoning,
        aiSelected: true
      } as any;
    })
    .filter(Boolean);

  return resolved;
}
